import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';
import { usePunishment } from './PunishmentContext';
import { safeParseArray } from '../utils/storageUtils';
import { toast } from 'sonner';
import { getStaffDisplayName } from '../utils/staffDisplay';
import { FLOW_CONFIG } from '../components/Tickets/TicketFlowConfig';

// Contexto de tickets para gerenciar estado dos tickets
const TicketContext = createContext();

// Hook personalizado para usar o contexto de tickets
export const useTickets = () => {
  const context = useContext(TicketContext);
  if (!context) {
    throw new Error('useTickets deve ser usado dentro de um TicketProvider');
  }
  return context;
};

// Função recursiva para remover campos undefined (Firestore não aceita undefined)
const sanitizeData = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(v => (v && typeof v === 'object') ? sanitizeData(v) : v);
  }

  const sanitized = {};
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value === undefined) return;

    if (value && typeof value === 'object' && !(value instanceof Date)) {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  });
  return sanitized;
};

// Função para gerar ID de ticket mais curto (10 caracteres alfanuméricos)
export const generateUniqueTicketId = () => {
  const timestamp = Date.now().toString(36); // Converte timestamp para base36
  const randomPart = Math.random().toString(36).substr(2, 4); // 4 caracteres aleatórios
  const shortId = (timestamp + randomPart).substr(-10).toUpperCase(); // Últimos 10 caracteres em maiúsculo
  return shortId;
};


const getDepartmentByCategory = (category) => {
  if (!category) return 'suporte';
  const categoryMap = {
    'suporte_duvidas': 'suporte',
    'cupons_descontos': 'suporte',
    'compras_doacoes': 'suporte',
    'contestar_punicao': 'moderacao',
    'denuncia_jogador': 'moderacao',
    'programacao': 'programacao',
    'denuncia_staff': 'admin',
    'bug_report': 'programacao',
    'sugestao': 'geral',
    'parceria': 'admin'
  };

  return categoryMap[category] || 'suporte';
};

// Função de filtragem baseada em permissões
const filterTicketsForUser = (allTickets, currentUser, accessibleCategories = []) => {
  if (!Array.isArray(allTickets)) return [];
  if (!currentUser) return [];

  // Admin / Head Staff / Direção vê tudo
  if (['admin', 'head_staff', 'direcao'].includes(currentUser.accessLevel)) {
    return allTickets;
  }

  // Usuário vê apenas seus próprios (pelo ID ou DiscordID)
  // E NUNCA vê tickets de escalação interna
  if (currentUser.accessLevel === 'user') {
    const uid = String(currentUser.id || '');
    const discordId = String(currentUser.discordId || '');
    return allTickets.filter(t => {
      // Guard clause for invalid ticket objects
      if (!t || typeof t !== 'object') return false;

      const isInternal = t.type === 'escalation' || t.category === 'escalacao_interna';
      if (isInternal) return false;

      return (
        String(t.userId) === uid ||
        String(t.userDiscordId) === discordId ||
        String(t.userId) === discordId
      );
    });
  }

  // Staffs específicos veem tickets de suas áreas + tickets escalados para eles + tickets que criaram
  // Utiliza as categorias acessíveis passadas pelo AuthContext (dinâmicas)
  const myCategories = accessibleCategories || [];

  return allTickets.filter(ticket => {
    if (!ticket || typeof ticket !== 'object') return false;

    // 1. É o autor?
    const isAuthor = String(ticket.userId) === String(currentUser.discordId);
    if (isAuthor) return true;

    // 2. É da minha categoria permitida?
    if (myCategories.includes(ticket.category) || myCategories.includes(ticket.department)) return true;

    // 3. Foi escalado ou atribuído a mim?
    if (ticket.assignedTo === currentUser.id || ticket.assignedTo === currentUser.discordId) return true;
    if (ticket.auxiliaryStatus && Array.isArray(ticket.auxiliaryStatus) && ticket.auxiliaryStatus.includes('escalado') && myCategories.includes(ticket.department)) return true;

    // 4. Se eu sou assignee
    if (ticket.assignees && Array.isArray(ticket.assignees) && ticket.assignees.some(a => a && (a.id === currentUser.id || a.discordId === currentUser.discordId))) return true;

    // 5. Permissão Global Staff: Validação de Vínculo
    if (ticket.title && (ticket.title.includes('Validação') || ticket.title.includes('Pré-Registro'))) return true;

    return false;
  });
};

// NOVA FUNÇÃO: Helper para Label de Cargo
const getRoleLabel = (accessLevel) => {
  const roleOptions = {
    'user': 'Usuário',
    'suporte': 'Suporte',
    'suporte_compras': 'Suporte Compras',
    'suporte_descontos': 'Suporte Descontos',
    'suporte_tecnico': 'Suporte Técnico',
    'moderador': 'Moderador',
    'super_moderador': 'Super Moderador',
    'programador': 'Programador',
    'head_suporte': 'Head Suporte',
    'head_moderacao': 'Head Moderação',
    'head_staff': 'Head Staff',
    'admin': 'Administrador',
    'direcao': 'Direção'
  };
  return roleOptions[accessLevel] || 'Staff';
};

export const TicketProvider = ({ children }) => {
  const { user, allUsers, getAccessibleCategories } = useAuth();
  const { punishments: punishmentsContextData } = usePunishment();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]); // [NEW] Ticket Categories

  // [NEW] Fetch Categories from Database or Fallback to partial initial state
  const fetchCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('ticket_categories')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) {
        console.warn('[TicketContext] Error fetching categories (table might not exist yet):', error);
        return;
      }

      if (data) {
        setCategories(data);
        // Tenta sincronizar se houver disparidade
        // syncCategories(data); // Chamamos separadamente para evitar loop se não controlado
      }
      return data;
    } catch (err) {
      console.error('[TicketContext] Unexpected error fetching categories:', err);
    }
  }, []);

  // [NEW] Sync Categories from Config to DB (Ensures Admin Panel has all options)
  const syncCategories = useCallback(async (currentCategories) => {
    try {
      if (!currentCategories) return;

      // 1. Get Canonical Categories from Code
      const startStep = FLOW_CONFIG['start'];
      if (!startStep || !startStep.options) return;

      const codeCategories = startStep.options;

      // 2. Identify Missing Categories in DB
      const missingCategories = codeCategories.filter(codeCat => {
        return !currentCategories.some(dbCat => dbCat.slug === codeCat.id);
      });

      if (missingCategories.length === 0) return;

      console.log('[TicketContext] Syncing missing categories to DB:', missingCategories);

      // 3. Insert Missing
      const newEntries = missingCategories.map((cat, index) => ({
        slug: cat.id,
        label: cat.label,
        description: cat.desc,
        icon: cat.icon?.displayName || 'HelpCircle', // Simple serialization
        is_active: true, // Default to true
        order_index: currentCategories.length + index
      }));

      const { error } = await supabase
        .from('ticket_categories')
        .insert(newEntries);

      if (error) {
        console.error('[TicketContext] Error syncing categories:', error);
      } else {
        // Refresh local state
        fetchCategories();
      }
    } catch (err) {
      console.error('[TicketContext] Error in syncCategories:', err);
    }
  }, [fetchCategories]); // Removed FLOW_CONFIG from dependency as it is external

  // Effect to sync once on load
  useEffect(() => {
    const init = async () => {
      const dbCats = await fetchCategories();
      if (dbCats) {
        syncCategories(dbCats);
      }
    };
    init();
  }, [fetchCategories, syncCategories]);

  // [NEW] Update Category
  const updateCategory = async (categoryId, updates) => {
    try {
      // Optimistic update
      setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, ...updates } : c));

      const { data, error } = await supabase
        .from('ticket_categories')
        .update(updates)
        .eq('id', categoryId)
        .select();

      if (error) throw error;
      return { success: true, data };
    } catch (err) {
      console.error('[TicketContext] Error updating category:', err);
      // Rollback
      fetchCategories();
      return { success: false, error: err.message };
    }
  };

  // Helper to normalize Ticket Data from DB (snake_case) to App (camelCase)
  const normalizeTicketData = (t) => {
    if (!t) return null;
    return {
      ...t,
      id: t.id,
      customId: t.custom_id || t.customId,
      displayId: t.display_id || t.displayId,
      userId: t.user_id || t.userId || t.userid,
      userDiscordId: t.user_discord_id || t.userDiscordId,
      userName: t.user_name || t.userName,
      userAvatar: t.user_avatar || t.userAvatar,
      formData: t.form_data || t.formData,
      createdAt: t.created_at || t.createdAt,
      lastUpdate: t.last_update || t.lastUpdate || t.lastupdate,
      assignedTo: t.assigned_to || t.assignedTo,
      assignedName: t.assigned_name || t.assignedName,
      assignedAvatar: t.assigned_avatar || t.assignedAvatar,
      auxiliaryStatus: t.auxiliary_status || t.auxiliaryStatus || [],
      escalationHistory: t.escalation_history || t.escalationHistory || [],
      playerId: t.player_id || t.playerId,
      characterName: t.character_name || t.characterName,
      // Keep other fields (title, description, status, priority, department, category, type, city, metadata, feedback, logs, responses, assignees)
      isArchivedQC: (t.auxiliary_status || t.auxiliaryStatus || []).includes('arquivado_qc'),
      qcAnalyzedBy: t.qc_analyzed_by || t.qcAnalyzedBy, // Ensure we map who analyzed it if available
      closedAt: t.closed_at || (['fechado', 'closed'].includes(t.status) ? (t.last_update || t.lastUpdate) : null),
      linkedTicketId: t.linked_ticket_id || t.linkedTicketId, // Added for escalation navigation
      recommendedResponseId: t.recommended_response_id || t.recommendedResponseId,
    };
  };

  // =================================================================================
  // REALTIME TICKETS SYNC (SUPABASE)
  // =================================================================================
  // Stable, idempotent subscription management
  const channelRef = useRef(null);

  const refreshTickets = useCallback(async (showLoading = false) => {
    if (!user) return;
    if (showLoading) setLoading(true);

    try {
      logger.debug('[TicketContext] Fetching tickets (Manual/Silent)...');
      const columns = [
        'id', 'custom_id', 'display_id', 'title', 'description', 'category',
        'department', 'priority', 'status', 'type', 'city', 'user_id',
        'user_discord_id', 'user_name', 'user_avatar', 'player_id',
        'character_name', 'assigned_to', 'assigned_name', 'assigned_avatar',
        'auxiliary_status', 'assignees', 'responses', 'logs',
        'escalation_history', 'form_data', 'metadata', 'feedback',
        'created_at', 'last_update', 'bot_state', 'is_bot_typing', 'linked_ticket_id', 'recommended_response_id'
      ].join(',');

      let query = supabase.from('tickets').select(columns);

      if (user.accessLevel === 'user') {
        query = query.eq('user_id', user.discordId);
      } else {
        query = query.order('id', { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        logger.error("[TicketContext] Error fetching tickets:", error);
        return;
      }

      const normalizedData = (data || []).map(normalizeTicketData);
      let filteredTickets = normalizedData;

      if (typeof filterTicketsForUser === 'function') {
        const accessibleCategories = getAccessibleCategories ? getAccessibleCategories() : [];
        filteredTickets = filterTicketsForUser(normalizedData, user, accessibleCategories);
      }

      if (Array.isArray(filteredTickets)) {
        filteredTickets.sort((a, b) => {
          const dateB = new Date(b.lastUpdate || b.createdAt || 0).getTime();
          const dateA = new Date(a.lastUpdate || a.createdAt || 0).getTime();
          return dateB - dateA;
        });
        setTickets(filteredTickets);
      }
    } catch (err) {
      logger.error("[TicketContext] Unexpected error in refreshTickets:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // Only subscribe if we have a logged-in user
    if (!user) {
      setTickets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    refreshTickets(true); // Initial load (ensure loading sets to false when done)

    // Cleanup previous channel if exists (Idempotent)
    if (channelRef.current) {
      logger.debug('[TicketContext] Cleaning up stale channel before subscribe');
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // Subscribe
    logger.debug('[TicketContext] Subscribing to tickets channel...');

    // 🚀 SECURITY: Scope subscription based on VERIFIED role
    const channelOptions = {
      event: '*',
      schema: 'public',
      table: 'tickets'
    };

    // If regular user, only listen to their own tickets to prevent broad data updates
    if (user.accessLevel === 'user') {
      const uid = user.discordId || user.id;
      channelOptions.filter = `user_id=eq.${uid}`;
    }

    const channel = supabase.channel(`tickets-realtime-${user.accessLevel}-${user.id}`)
      .on('postgres_changes', channelOptions, (payload) => {
        logger.debug('[TicketContext] Realtime Update:', payload.eventType);
        refreshTickets(false); // Silent refresh
      })
      .subscribe((status) => {
        logger.debug('[TicketContext] Realtime subscription status:', status);
      });

    channelRef.current = channel;

    // Cleanup on unmount or user change
    return () => {
      logger.debug('[TicketContext] Unmounting/Changing User - Cleanup Channel');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user, refreshTickets]);



  // =================================================================================
  // ACTIONS (CREATE, UPDATE, ETC)
  // =================================================================================

  // Helper to log staff activity on meaningful actions
  const logStaffActivity = () => {
    if (user && user.accessLevel !== 'user' && user.id) {
      // Fire and forget
      supabase.from('users').update({ last_login: new Date().toISOString() }).eq('id', user.id).then();
    }
  };

  const createTicket = async (ticketData) => {
    console.log('[TicketContext] Creating ticket with v2 logic (SnakeCase fix)...');
    setLoading(true);
    try {
      const customId = ticketData.id || generateUniqueTicketId(); // Use provided ID or generate new one
      const displayId = customId;

      const supabasePayload = {
        id: customId,
        custom_id: customId,
        display_id: displayId,
        title: ticketData.title || `Ticket - ${ticketData.category}`,
        category: ticketData.category,
        status: 'aguardando_sistema',
        auxiliary_status: [],
        priority: ticketData.priority || 'normal',
        department: ticketData.department || getDepartmentByCategory(ticketData.category),

        // Dados do Autor - Use ticketData if provided (for summons), otherwise current user
        user_id: ticketData.userId || user.discordId,
        user_discord_id: ticketData.userDiscordId || ticketData.userId || user.discordId,
        user_name: ticketData.authorName || user.name || user.username || 'Usuário',
        user_avatar: ticketData.userAvatar || user.avatar || null,

        // Dados do Ticket
        city: ticketData.city || null,
        description: ticketData.description,
        form_data: ticketData,
        type: ticketData.type || null,
        metadata: ticketData.metadata || null,

        // Timestamps
        created_at: new Date().toISOString(),
        last_update: new Date().toISOString(),

        // Controle
        assigned_to: null,
        assignees: [],
        responses: [],
        escalation_history: [],
        logs: [
          {
            id: Date.now(),
            type: 'creation',
            message: 'Ticket criado',
            userId: user.discordId,
            userName: user.name,
            userRole: 'user',
            timestamp: new Date().toISOString()
          }
        ],

        // Player Info
        player_id: ticketData.playerId || null,
        character_name: ticketData.characterName || null,

        // Linking
        linked_ticket_id: ticketData.linkedTicketId || null,
      };

      // Deterministic Create: Insert + Select Single immediately
      const { data, error } = await supabase
        .from('tickets')
        .insert(sanitizeData(supabasePayload))
        .select()
        .single();

      if (error) throw error;

      const newTicketNormalized = normalizeTicketData(data);

      // Optimistic Update: Add to local state immediately so TicketRoute finds it
      setTickets(prev => [newTicketNormalized, ...prev]);

      return { success: true, ticket: newTicketNormalized, ticketId: data.id };
    } catch (error) {
      logger.error('Erro ao criar ticket:', error);
      return { success: false, error: 'Erro ao criar ticket na nuvem.' };
    } finally {
      setLoading(false);
    }
  };

  const updateTicket = async (ticketId, updates) => {
    // Snapshot anterior para rollback
    const previousTickets = [...tickets];

    logStaffActivity();

    try {
      const updatePayload = {
        ...updates,
        last_update: new Date().toISOString() // Payload Snake Case (Supabase)
      };

      // Payload camelCase para estado local
      const localUpdates = {};
      Object.keys(updates).forEach(key => {
        // Mapeamento simples de snake_case para camelCase se necessário
        // Como o normalize já trata, podemos tentar manter ou converter
        // Para simplificar, assumimos que updates vêm mistos ou compatíveis
        // O ideal é normalizar keys aqui ou aceitar o que vier
        // Vamos assumir que updates podem vir como 'bot_state' ou 'botState'
        const camelKey = key.replace(/_([a-z])/g, g => g[1].toUpperCase());
        localUpdates[camelKey] = updates[key];
      });
      localUpdates.lastUpdate = new Date().toISOString();

      // OPTIMISTIC UPDATE
      setTickets(prev => prev.map(t => {
        if (t.id === ticketId) {
          return { ...t, ...localUpdates };
        }
        return t;
      }));

      const { error } = await supabase.from('tickets').update(sanitizeData(updatePayload)).eq('id', ticketId);
      if (error) throw error;

      return { success: true };
    } catch (error) {
      logger.error('Erro ao atualizar ticket:', error);
      // ROLLBACK
      setTickets(previousTickets);
      return { success: false, error: 'Falha ao atualizar ticket.' };
    }
  };

  /**
   * Tenta "reivindicar" a execução do bot para um ticket específico.
   * Usa uma verificação atômica no banco para garantir que apenas um cliente execute.
   */
  const claimBotTask = async (ticketId) => {
    let success = false;
    try {
      const { data, error } = await supabase
        .from('tickets')
        .update({
          bot_state: { status: 'starting', claimed_at: new Date().toISOString() },
          last_update: new Date().toISOString()
        })
        .eq('id', ticketId)
        .or('bot_state.is.null,bot_state->>status.eq.pending,bot_state->>status.is.null')
        .select();

      if (error) {
        console.error('[TicketContext] Error claiming bot task (Supabase):', error);
        throw error;
      }

      success = data && data.length > 0;
      console.log(`[TicketContext] Claim result for ${ticketId}: success=${success}`, data);

      if (success) {
        const normalized = normalizeTicketData(data[0]);
        setTickets(prev => prev.map(t => t.id === ticketId ? normalized : t));
      } else {
        // Debugar por que falhou
        const { data: currentTicket } = await supabase.from('tickets').select('bot_state').eq('id', ticketId).single();
        console.log(`[TicketContext] Claim failed for ${ticketId}. Current DB State:`, currentTicket?.bot_state);

        if (currentTicket) {
          const normalized = normalizeTicketData(currentTicket);
          setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, botState: normalized.botState, bot_state: normalized.bot_state } : t));
        }
      }
      return { success };
    } catch (error) {
      console.error('[TicketContext] Error claiming bot task:', error);
      return { success: false };
    }
  };

  const addTicketResponse = async (ticketId, message, isInternal = false, customAuthor = null) => {
    console.log('[TicketContext] addTicketResponse called:', { ticketId, message, isInternal, customAuthor });
    logStaffActivity();
    try {
      // 1. Ler ticket atual
      const { data: ticketData, error: fetchError } = await supabase.from('tickets').select('*').eq('id', ticketId).single();
      if (fetchError || !ticketData) throw new Error('Ticket não encontrado');

      const isStaff = user.accessLevel !== 'user';
      const authorName = (isStaff && !customAuthor)
        ? getStaffDisplayName(user)
        : (customAuthor || user.name || 'Usuário');

      const newResponse = {
        id: Date.now(),
        message: message,
        isInternal: isInternal,
        createdAt: new Date().toISOString(),
        userId: customAuthor ? 'system' : (user.discordId || 'unknown'),
        userName: authorName,
        author: authorName,
        authorAvatar: customAuthor ? null : (user.avatar ? `https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png` : null),
        authorRole: customAuthor ? 'system' : user.accessLevel,
        isStaff: customAuthor ? false : isStaff,
        isBot: !!customAuthor
      };

      const newLog = {
        id: Date.now(),
        type: 'response',
        message: 'Respondeu o ticket',
        userId: customAuthor ? 'system' : user.discordId,
        userName: customAuthor || user.name,
        userRole: customAuthor ? 'System' : (user.accessLevel || 'User'),
        timestamp: new Date().toISOString()
      };

      // OPTIMISTIC UPDATE: Update local state BEFORE saving to database
      setTickets(prevTickets => {
        return prevTickets.map(t => {
          if (t.id === ticketId) {
            return {
              ...t,
              responses: [...(t.responses || []), newResponse],
              logs: [...(t.logs || []), newLog],
              lastUpdate: new Date().toISOString()
            };
          }
          return t;
        });
      });

      // 2. Save to database
      const { error: updateError } = await supabase.from('tickets').update(sanitizeData({
        responses: [...(ticketData.responses || []), newResponse],
        logs: [...(ticketData.logs || []), newLog],
        last_update: new Date().toISOString()
      })).eq('id', ticketId);

      if (updateError) {
        // ROLLBACK: If save fails, remove the optimistic update
        setTickets(prevTickets => {
          return prevTickets.map(t => {
            if (t.id === ticketId) {
              return {
                ...t,
                responses: (t.responses || []).filter(r => r.id !== newResponse.id),
                logs: (t.logs || []).filter(l => l.id !== newLog.id)
              };
            }
            return t;
          });
        });
        throw updateError;
      }

      return { success: true };
    } catch (error) {
      logger.error('Erro ao responder ticket:', error);
      return { success: false, error: 'Falha ao enviar resposta.' };
    }
  };

  const deleteTicketResponse = async (ticketId, responseId) => {
    try {
      // 1. Ler ticket atual
      const { data: ticketData, error: fetchError } = await supabase.from('tickets').select('*').eq('id', ticketId).single();
      if (fetchError || !ticketData) throw new Error('Ticket não encontrado');

      const currentResponses = ticketData.responses || [];
      const responseToDelete = currentResponses.find(r => r.id === responseId);

      if (!responseToDelete) throw new Error('Resposta não encontrada');

      const updatedResponses = currentResponses.filter(r => r.id !== responseId);
      const newLog = {
        id: Date.now(),
        type: 'response_deleted',
        message: 'Removeu uma resposta',
        userId: user.discordId,
        userName: user.name,
        userRole: user.accessLevel,
        timestamp: new Date().toISOString()
      };

      // OPTIMISTIC UPDATE
      setTickets(prevTickets => {
        return prevTickets.map(t => {
          if (t.id === ticketId) {
            return {
              ...t,
              responses: updatedResponses,
              logs: [...(t.logs || []), newLog],
              lastUpdate: new Date().toISOString()
            };
          }
          return t;
        });
      });

      // 2. Save to database
      const { error: updateError } = await supabase.from('tickets').update(sanitizeData({
        responses: updatedResponses,
        logs: [...(ticketData.logs || []), newLog],
        last_update: new Date().toISOString()
      })).eq('id', ticketId);

      if (updateError) throw updateError;

      return { success: true };
    } catch (error) {
      logger.error('Erro ao deletar resposta:', error);
      return { success: false, error: 'Falha ao deletar resposta.' };
    }
  };

  const addTicketFeedback = async (ticketId, feedbackData) => {
    // Optimistic Update
    const previousTickets = [...tickets];
    setTickets(prev => prev.map(t => t.id === ticketId ? {
      ...t,
      feedback: feedbackData,
      lastUpdate: new Date().toISOString()
    } : t));

    try {
      const { error } = await supabase.from('tickets').update({
        feedback: feedbackData,
        last_update: new Date().toISOString()
      }).eq('id', ticketId);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      logger.error('Erro ao enviar feedback:', error);
      // Rollback
      setTickets(previousTickets);
      return { success: false };
    }
  };

  /**
   * Assumir um ticket (Staff)
   */
  const claimTicket = async (ticketId, checkUnassigned = false) => {
    try {
      if (!user) return { success: false, error: 'Usuário não autenticado' };
      logStaffActivity();

      // Snapshot anterior para rollback
      const previousTickets = [...tickets];

      const updateData = {
        assigned_to: user.discordId,
        assigned_name: getStaffDisplayName(user),
        assigned_avatar: user.avatar || null,
        status: 'em_andamento',
        auxiliary_status: ['em_andamento'],
        last_update: new Date().toISOString()
      };

      const newLog = {
        id: Date.now(),
        type: 'claim',
        message: 'Assumiu o ticket',
        userId: user.discordId,
        userName: user.name,
        userRole: user.accessLevel,
        timestamp: new Date().toISOString()
      };

      const newSystemResponse = {
        id: Date.now() + 1,
        message: `⚡ **STAFF RESPONSÁVEL DEFINIDO**\n\nO atendente **${getStaffDisplayName(user)}** vai continuar te atendendo a partir de agora.`,
        isInternal: false,
        createdAt: new Date().toISOString(),
        userId: 'system',
        userName: 'SISTEMA',
        author: 'SISTEMA',
        authorAvatar: null,
        authorRole: 'system',
        isStaff: false,
        isBot: true
      };

      // 1. Fetch current ticket data to append logs and responses
      const { data: ticketData, error: fetchError } = await supabase
        .from('tickets')
        .select('logs, responses, assignees, assigned_to')
        .eq('id', ticketId)
        .single();

      if (fetchError && !ticketData) throw fetchError;

      // Verification for Race Condition (Pre-flight check)
      if (checkUnassigned && ticketData.assigned_to) {
        return { success: false, error: 'Este ticket já foi assumido por outro staff.' };
      }

      const currentLogs = ticketData?.logs || [];
      const currentResponses = ticketData?.responses || [];

      const updatedLogs = [...currentLogs, newLog];
      const updatedResponses = [...currentResponses, newSystemResponse];
      const updatedAssignees = [{
        discordId: user.discordId,
        name: getStaffDisplayName(user),
        avatar: user.avatar || null
      }];

      // OPTIMISTIC UPDATE
      setTickets(prev => prev.map(t => {
        if (t.id === ticketId) {
          return {
            ...t,
            assignedTo: user.discordId,
            assignedName: getStaffDisplayName(user),
            assignedAvatar: user.avatar || null,
            status: 'em_andamento',
            auxiliaryStatus: ['em_andamento'],
            assignees: updatedAssignees,
            logs: updatedLogs,
            responses: updatedResponses,
            lastUpdate: new Date().toISOString()
          };
        }
        return t;
      }));

      // 2. Persist to DB with Safety Check
      let query = supabase.from('tickets').update({
        ...updateData,
        assignees: updatedAssignees,
        logs: updatedLogs,
        responses: updatedResponses
      }).eq('id', ticketId);

      // If we must ensure it's unassigned, we add that condition to the UPDATE query
      if (checkUnassigned) {
        query = query.is('assigned_to', null);
      }

      const { data, error, count } = await query.select(); // Select to check if row was returned

      if (error) {
        // ROLLBACK
        setTickets(previousTickets);
        throw error;
      }

      // If checkUnassigned was true, and no rows were updated, it means the condition failed
      if (checkUnassigned && (!data || data.length === 0)) {
        setTickets(previousTickets); // Rollback
        return { success: false, error: 'Este ticket já foi assumido por outro staff.' };
      }

      return { success: true };
    } catch (error) {
      console.error('Erro ao assumir ticket:', error);
      return { success: false, error: 'Erro ao assumir ticket.' };
    }
  };

  const deleteTicket = async (ticketId) => {
    try {
      const { error } = await supabase.from('tickets').delete().eq('id', ticketId);
      if (error) throw error;
      // Optimistic remove
      setTickets(prev => prev.filter(t => t.id !== ticketId));
      return { success: true };
    } catch (error) {
      console.error('Erro ao excluir ticket:', error);
      return { success: false, error: 'Erro ao excluir ticket.' };
    }
  };

  const deleteAllTicketsFromUser = async (userId, userDiscordId) => {
    try {
      console.log(`[TicketContext] Deleting all tickets for user ${userId} / ${userDiscordId}`);

      const targetId = userDiscordId || userId;
      if (!targetId) return { success: false, error: 'ID de usuário inválido' };

      const { error } = await supabase
        .from('tickets')
        .delete()
        .or(`user_id.eq.${targetId},user_discord_id.eq.${targetId}`);

      if (error) throw error;

      setTickets(prev => prev.filter(t =>
        String(t.userId) !== String(targetId) &&
        String(t.userDiscordId) !== String(targetId)
      ));

      return { success: true, deletedCount: 'unknown' };
    } catch (error) {
      console.error('Erro ao excluir todos os tickets do usuário:', error);
      return { success: false, error: 'Erro ao excluir tickets do banco de dados.' };
    }
  };

  const archiveTicketQC = async (ticketId, staffUser) => {
    try {
      const timestamp = new Date().toISOString();
      const staffName = staffUser?.name || staffUser?.username || 'Staff';

      // 1. Create Log Entry
      const newLog = {
        id: Date.now(),
        type: 'qc_analysis',
        message: `Análise de Qualidade concluída por ${staffName}`,
        userId: staffUser?.discordId || 'system',
        userName: staffName,
        userRole: staffUser?.accessLevel || 'staff',
        timestamp: timestamp
      };

      // 2. Fetch current logs to append
      const { data: ticketData, error: fetchError } = await supabase
        .from('tickets')
        .select('logs')
        .eq('id', ticketId)
        .single();

      if (fetchError) throw fetchError;

      const updatedLogs = [...(ticketData?.logs || []), newLog];

      // 3. Update Ticket
      const { error } = await supabase.from('tickets').update({
        status: 'fechado',
        auxiliary_status: ['arquivado_qc'],
        qc_analyzed_by: staffName, // Save who analyzed
        logs: updatedLogs,
        last_update: timestamp
      }).eq('id', ticketId);

      if (error) throw error;

      // Optimistic Update
      setTickets(prev => prev.map(t => {
        if (t.id === ticketId) {
          return {
            ...t,
            status: 'fechado',
            auxiliaryStatus: ['arquivado_qc'],
            qcAnalyzedBy: staffName,
            logs: updatedLogs,
            lastUpdate: timestamp
          };
        }
        return t;
      }));

      return { success: true };
    } catch (error) {
      console.error("Erro ao arquivar QC:", error);
      return { success: false, error: 'Erro ao finalizar controle de qualidade.' };
    }
  };

  const recommendResponse = async (ticketId, responseId) => {
    try {
      // 1. Fetch current logs
      const { data: ticketData, error: fetchError } = await supabase
        .from('tickets')
        .select('logs')
        .eq('id', ticketId)
        .single();

      if (fetchError) throw fetchError;

      const newLog = {
        id: Date.now(),
        type: 'solution_marked',
        message: 'Marcou uma resposta como solução',
        userId: user.discordId,
        userName: getStaffDisplayName(user),
        userRole: user.accessLevel,
        timestamp: new Date().toISOString()
      };

      const updatedLogs = [...(ticketData.logs || []), newLog];

      // Optimistic Update
      const previousTickets = [...tickets];
      setTickets(prev => prev.map(t => {
        if (t.id === ticketId) {
          return {
            ...t,
            recommendedResponseId: responseId,
            logs: updatedLogs,
            lastUpdate: new Date().toISOString()
          };
        }
        return t;
      }));

      const { error } = await supabase.from('tickets').update({
        recommended_response_id: responseId,
        logs: updatedLogs,
        last_update: new Date().toISOString()
      }).eq('id', ticketId);

      if (error) {
        setTickets(previousTickets);
        throw error;
      }
      return { success: true };
    } catch (error) {
      console.error('Erro ao marcar solução:', error);
      return { success: false, error: 'Erro ao marcar solução.' };
    }
  };

  const unrecommendResponse = async (ticketId) => {
    try {
      // 1. Fetch current logs
      const { data: ticketData, error: fetchError } = await supabase
        .from('tickets')
        .select('logs')
        .eq('id', ticketId)
        .single();

      if (fetchError) throw fetchError;

      const newLog = {
        id: Date.now(),
        type: 'solution_unmarked',
        message: 'Desmarcou a solução',
        userId: user.discordId,
        userName: getStaffDisplayName(user),
        userRole: user.accessLevel,
        timestamp: new Date().toISOString()
      };

      const updatedLogs = [...(ticketData.logs || []), newLog];

      // Optimistic Update
      const previousTickets = [...tickets];
      setTickets(prev => prev.map(t => {
        if (t.id === ticketId) {
          return {
            ...t,
            recommendedResponseId: null,
            logs: updatedLogs,
            lastUpdate: new Date().toISOString()
          };
        }
        return t;
      }));

      const { error } = await supabase.from('tickets').update({
        recommended_response_id: null,
        logs: updatedLogs,
        last_update: new Date().toISOString()
      }).eq('id', ticketId);

      if (error) {
        setTickets(previousTickets);
        throw error;
      }
      return { success: true };
    } catch (error) {
      console.error('Erro ao desmarcar solução:', error);
      return { success: false, error: 'Erro ao desmarcar solução.' };
    }
  };

  // Helper para buscar TODOS os tickets de um usuário (ignora filtros de permissão)
  const getUserTickets = (userId) => {
    // Implementação futura se necessário buscar async
    // Por enquanto, usa o filtro local do que já foi baixado ou faz query direta
    // Para simplificar e manter API
    if (!tickets) return [];
    return tickets.filter(t => String(t.userId) === String(userId) || String(t.userDiscordId) === String(userId));
  };

  // Check Player ID Helper (REAL)
  const checkPlayerId = async (city, id) => {
    console.log(`[TicketContext] Buscando player ${id} em ${city}...`);

    // Se allUsers ainda não carregou ou não é array, falha silenciosamente
    if (!allUsers || !Array.isArray(allUsers)) {
      console.warn('[TicketContext] allUsers não disponível ou inválido.');
      return { found: false };
    }

    const searchId = String(id).trim();

    try {
      // Busca no array de usuários em memória (sincronizado pelo AuthContext)
      for (const u of allUsers) {
        if (u.cities && Array.isArray(u.cities)) {
          const char = u.cities.find(c =>
            String(c.playerId).trim() === searchId &&
            (city ? String(c.city).toLowerCase() === String(city).toLowerCase() : true)
          );

          if (char) {
            console.log(`[TicketContext] Jogador encontrado: ${u.name} (Char: ${char.characterName})`);

            // Recria estrutura de retorno compatível com UI
            // Nota: Punishments agora buscados do Contexto em tempo real (Supabase)
            const allPunishments = punishmentsContextData;

            return {
              found: true,
              player: {
                characterId: char.playerId,
                name: char.characterName || u.name,
                realName: u.name,
                discord: u.discordId,
                discordId: u.discordId,
                avatar: u.avatar,
                lastTickets: [],
                punishments: allPunishments
                  .filter(p => p.userId === u.discordId)
                  .map(p => ({
                    type: p.punishmentType,
                    internalReason: p.internalReason || 'Sem motivo interno',
                    date: new Date(p.appliedAt).toLocaleDateString('pt-BR')
                  }))
                  .reverse(),
                stats: {
                  totalPunishments: allPunishments.filter(p => p.userId === u.discordId).length,
                  totalTickets: 0 // Cloud tickets count is expensive to calc here separately, keeping 0 for perf
                }
              }
            };
          }
        }
      }
    } catch (e) {
      console.error("[TicketContext] Erro ao buscar player:", e);
      return { found: false };
    }

    return { found: false };
  };

  // FAQS (Supabase)
  useEffect(() => {
    if (!user) return;

    const fetchFaqs = async () => {
      try {
        const { data, error } = await supabase
          .from('faqs')
          .select('*')
          .order('id', { ascending: false });

        if (error) {
          console.error("[TicketContext] Error fetching FAQs:", error);
        } else {
          setFaqs(data || []);
        }
      } catch (err) {
        console.error("[TicketContext] Unexpected error fetching FAQs:", err);
      }
    };

    fetchFaqs();

    const channel = supabase.channel('faqs-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'faqs' }, (payload) => {
        fetchFaqs();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const addFAQ = async (faqData) => {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .insert([{
          question: faqData.question,
          answer: faqData.answer,
          category: faqData.category,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return { success: true, faq: data };
    } catch (error) {
      console.error("[TicketContext] Error adding FAQ:", error);
      return { success: false, error: error.message };
    }
  };

  const updateFAQ = async (faqId, updateData) => {
    try {
      const { error } = await supabase
        .from('faqs')
        .update({
          question: updateData.question,
          answer: updateData.answer,
          category: updateData.category
        })
        .eq('id', faqId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error("[TicketContext] Error updating FAQ:", error);
      return { success: false, error: error.message };
    }
  };

  const deleteFAQ = async (faqId) => {
    try {
      const { error } = await supabase
        .from('faqs')
        .delete()
        .eq('id', faqId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error("[TicketContext] Error deleting FAQ:", error);
      return { success: false, error: error.message };
    }
  };

  // Helper de acesso a ticket (usado em notificações e filtros)
  const canAccessTicket = useCallback((ticket) => {
    if (!ticket || !user) return false;

    // Admin / Head Staff / Direção vê tudo
    if (['admin', 'head_staff', 'direcao'].includes(user.accessLevel)) {
      return true;
    }

    // Role permissions mapping
    const rolePermissions = {
      'suporte': ['suporte_duvidas', 'programacao', 'suporte'],
      'suporte_compras': ['compras_doacoes'],
      'suporte_descontos': ['cupons_descontos'],
      'moderador': ['denuncia_jogador', 'moderacao'],
      'super_moderador': ['denuncia_jogador', 'contestar_punicao', 'contestar_banimento', 'moderacao'],
      'programador': ['programacao', 'bug_report'],
      'head_suporte': ['suporte_duvidas', 'compras_doacoes', 'cupons_descontos', 'programacao', 'bug_report', 'suporte'],
      'head_moderacao': ['denuncia_jogador', 'contestar_punicao', 'contestar_banimento', 'moderacao'],
      'analista_qualidade': ['controle_qualidade'],
      'departamento_juridico': ['juridico']
    };

    const myCategories = rolePermissions[user.accessLevel] || [];

    // 1. É o autor?
    const isAuthor = String(ticket.userId) === String(user.discordId);
    if (isAuthor) return true;

    // 2. É da minha categoria permitida?
    if (myCategories.includes(ticket.category) || myCategories.includes(ticket.department)) return true;

    // 3. Foi escalado ou atribuído a mim?
    if (ticket.assignedTo === user.id || ticket.assignedTo === user.discordId) return true;
    if (ticket.auxiliaryStatus && Array.isArray(ticket.auxiliaryStatus) && ticket.auxiliaryStatus.includes('escalado') && myCategories.includes(ticket.department)) return true;

    // 4. Se eu sou assignee
    if (ticket.assignees && Array.isArray(ticket.assignees) && ticket.assignees.some(a => a && (a.id === user.id || a.discordId === user.discordId))) return true;

    // 5. Permissão Global Staff: Validação de Vínculo
    if (ticket.title && (ticket.title.includes('Validação') || ticket.title.includes('Pré-Registro'))) return true;

    return false;
  }, [user]);

  // Helper para buscar tickets similares (usado no TicketCreate)
  const getSimilarTickets = (text) => {
    if (!text || typeof text !== 'string' || text.length < 5 || !Array.isArray(tickets)) return [];

    // Divide o texto em palavras-chave ignorando palavras comuns
    const stopWords = ['o', 'a', 'de', 'da', 'do', 'em', 'para', 'com', 'não', 'que', 'é', 'um', 'uma'];
    const keywords = text.toLowerCase()
      .split(/[\s,.]+/)
      .filter(w => w.length > 3 && !stopWords.includes(w));

    if (keywords.length === 0) return [];

    return tickets.filter(t => {
      // Ignora tickets do próprio usuário e tickets fechados
      if (!t || t.userId === user?.discordId || ['resolvido', 'fechado', 'cancelled'].includes(t.status)) return false;

      const tTitle = (t.title || '').toLowerCase();
      const tDesc = (t.description || '').toLowerCase();

      // Pontuação simples
      let score = 0;
      keywords.forEach(kw => {
        if (tTitle.includes(kw)) score += 2;
        if (tDesc.includes(kw)) score += 1;
      });

      return score >= 2; // Pelo menos uma palavra no título ou duas na descrição
    })
      .slice(0, 3); // Retorna top 3
  };

  // --- NOVA FUNÇÃO: Atribuição Automática com Safety Check ---
  const assignNextTicket = async (filters = {}) => {
    try {
      if (!user) return { success: false, error: 'Usuário não autenticado' };

      // 1. Filtrar Tickets Elegíveis
      let candidates = tickets.filter(t => {
        // Status deve ser 'aberto' (não 'aguardando_sistema', não 'fechado')
        if (t.status !== 'aberto') return false;

        // Não pode ter ninguém atribuído
        if (t.assignedTo) return false;

        // Apply filters
        if (filters.department && filters.department !== 'all' && t.category !== filters.department) return false;
        if (filters.priority && filters.priority !== 'all') {
          // Basic priority check (can be improved with array check like in StaffNewTickets)
          if (t.priority !== filters.priority &&
            !(filters.priority === 'normal' && ['media', 'medium'].includes(t.priority)) &&
            !(filters.priority === 'alta' && ['high', 'urgent', 'urgente'].includes(t.priority))
          ) {
            // Relaxed match failed 
          }
        }
        if (filters.city && filters.city !== 'all' && t.city !== filters.city) return false;

        return true;
      });

      // 2. Ordenar: Mais Antigos Primeiro (FIFO) e Prioridade
      candidates.sort((a, b) => {
        // Prioridade numérica (Urgente > Alta > Normal > Baixa)
        const getPrio = (p) => {
          const val = (p || '').toLowerCase();
          if (val.includes('urgente') || val.includes('urgent')) return 4;
          if (val.includes('alta') || val.includes('high')) return 3;
          if (val.includes('media') || val.includes('medium') || val.includes('normal')) return 2;
          return 1;
        };
        const pA = getPrio(a.priority);
        const pB = getPrio(b.priority);
        if (pA !== pB) return pB - pA; // Maior prioridade primeiro

        // Desempate por data (Mais antigo primeiro)
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateA - dateB;
      });

      if (candidates.length === 0) {
        return { success: false, error: 'Nenhum ticket disponível para os filtros selecionados.' };
      }

      // 3. Tentar assumir o primeiro (com verificação de segurança)
      // Se falhar, tentamos o próximo recursivamente ou loop
      // Vamos tentar os 3 primeiros candidatos para evitar chamadas infinitas

      const maxRetries = Math.min(candidates.length, 3);

      for (let i = 0; i < maxRetries; i++) {
        const targetTicket = candidates[i];

        console.log(`[TicketContext] Tentando assumir ticket ${targetTicket.id} (Tentativa ${i + 1}/${maxRetries})...`);

        // Chamada com flag checkUnassigned = true
        const result = await claimTicket(targetTicket.id, true);

        if (result.success) {
          return { success: true, ticketId: targetTicket.id };
        } else {
          // Se falhar e for erro de "já assumido", continuamos para o próximo
          // Se for outro erro, paramos
          if (result.error && !result.error.includes('já foi assumido')) {
            return result;
          }
          console.warn(`[TicketContext] Ticket ${targetTicket.id} já foi pego. Tentando próximo...`);
        }
      }

      return { success: false, error: 'Todos os tickets tentados já foram assumidos. Por favor, tente novamente.' };

    } catch (err) {
      console.error("Erro no assignNextTicket:", err);
      return { success: false, error: 'Erro interno ao atribuir ticket.' };
    }
  };

  return (
    <TicketContext.Provider value={{
      tickets,
      loading,
      refreshTickets,
      createTicket,
      updateTicket,

      deleteTicket, // Nova função
      deleteAllTicketsFromUser,
      addTicketResponse,
      deleteTicketResponse,
      addTicketFeedback,
      getUserTickets,
      checkPlayerId,
      claimTicket,
      claimBotTask,
      assignNextTicket, // EXPOSED
      archiveTicketQC, // Nova função
      recommendResponse, // Stub
      unrecommendResponse, // Stub
      getSimilarTickets, // Adicionado ao contexto
      categories, // [NEW]
      updateCategory, // [NEW]
      canAccessTicket, // Adicionado para corrigir crash nas notificações
      // FAQs
      faqs,
      addFAQ,
      updateFAQ,
      deleteFAQ
    }}>
      {children}
    </TicketContext.Provider>
  );
};
